import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { CircleAlertIcon, CircleCheckIcon, Loader2Icon, TriangleAlertIcon } from "lucide-react";
import { type ReactNode, useId, useMemo, useState } from "react";

import type { EntityLink } from "~/ai-chat/model/entity-link";
import {
  allowedExtensionsFor,
  baseNameOf,
  extensionOf,
  isValidSaveResourceFileName,
  SAVE_RESOURCE_MAX_FILE_NAME_LENGTH,
  type SaveResourceAction,
} from "~/ai-chat/model/save-resource-action";
import { ChatArtifactCard } from "~/ai-chat/ui/chat-artifact-card";
import { ChatEntityChip } from "~/ai-chat/ui/chat-entity-chip";
import { ChatInfosecLevelSelect, type InfosecLevelOption } from "~/ai-chat/ui/chat-infosec-level-select";
import { formatBytes } from "~/utils/format-bytes";

export type { InfosecLevelOption } from "~/ai-chat/ui/chat-infosec-level-select";

export interface SaveDestinationOption {
  systemName: string;
  branchName: string;
}

export interface SaveResourceCardChoices {
  // Unset = the platform default level, as a regular upload gets.
  infosecLevelId?: string;
  saveToSystem: boolean;
}

export type SaveResourceCardState =
  | { status: "ready" }
  | { status: "saving" }
  | { status: "saved"; link: EntityLink; destination?: string; warning?: string }
  | { status: "error"; message: string };

interface ChatSaveResourceCardProps {
  action: SaveResourceAction;
  state: SaveResourceCardState;
  // undefined = the platform infosec setting hasn't resolved yet; saving stays
  // blocked so the level picker is on screen before anything is written.
  infosecEnabled: boolean | undefined;
  infosecLevels: InfosecLevelOption[];
  defaultInfosecLevel?: InfosecLevelOption | null;
  // The system the chat is open on. null = library only; undefined = still resolving,
  // which also blocks saving so a file never lands in the library by accident.
  destination: SaveDestinationOption | null | undefined;
  // Classification pickers that fetch their own options are composed by the feature layer.
  controlTagsField?: ReactNode;
  onSave: (action: SaveResourceAction, choices: SaveResourceCardChoices) => void;
}

export function ChatSaveResourceCard({
  action,
  state,
  infosecEnabled,
  infosecLevels,
  defaultInfosecLevel,
  destination,
  controlTagsField,
  onSave,
}: ChatSaveResourceCardProps) {
  const nameId = useId();
  const infosecLevelSelectId = useId();
  const destinationSelectId = useId();
  // The extension stays what the assistant or tool produced; the user renames only the stem, which
  // is also the display name, as with a regular upload.
  const extension = extensionOf(action.fileName);
  const [name, setName] = useState(baseNameOf(action.fileName));
  const [infosecLevelId, setInfosecLevelId] = useState<string | null>(null);
  const [saveToSystem, setSaveToSystem] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // A staged document was rendered by a tool; its bytes never reach the chat, so there is no
  // text to preview and the size comes from the upload.
  const content = action.content ?? "";
  const lines = useMemo(() => content.split("\n"), [content]);
  const byteSize = useMemo(() => action.staged?.size ?? new Blob([content]).size, [action.staged, content]);
  const isTruncated = lines.length > PREVIEW_LINE_COUNT;
  const preview = expanded || !isTruncated ? content : lines.slice(0, PREVIEW_LINE_COUNT).join("\n");

  const trimmedName = name.trim();
  const fileName = extension ? `${trimmedName}.${extension}` : trimmedName;
  const isNameValid = isValidSaveResourceFileName(fileName, allowedExtensionsFor(action));
  const canSave = isNameValid && infosecEnabled !== undefined && destination !== undefined;
  const isLocked = state.status === "saving" || state.status === "saved";

  const requestSave = () => {
    if (!canSave) return;
    onSave(
      { ...action, fileName, displayName: trimmedName },
      { infosecLevelId: infosecLevelId ?? undefined, saveToSystem: destination !== null && saveToSystem },
    );
  };

  return (
    <div className="border-border bg-card my-1 w-full rounded-lg border p-2.5 font-sans first:mt-0 last:mb-0">
      {state.status === "saved" ? (
        <ChatArtifactCard
          name={fileName}
          kind={extension}
          meta={formatBytes(byteSize)}
          className="border-0 bg-transparent p-0"
        />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor={nameId} className="text-xs">
              Resource name
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={nameId}
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isLocked}
                aria-invalid={!isNameValid}
                className="h-7 flex-1 text-xs"
              />
              <span className="text-muted-foreground bg-background/60 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase">
                {extension || "—"}
              </span>
              <span className="text-muted-foreground shrink-0 text-[11px]">{formatBytes(byteSize)}</span>
            </div>
            {!isNameValid && trimmedName.length > 0 && (
              <p className="text-destructive text-xs">
                {fileName.length > SAVE_RESOURCE_MAX_FILE_NAME_LENGTH
                  ? `Use a name under ${SAVE_RESOURCE_MAX_FILE_NAME_LENGTH} characters, including the extension.`
                  : "Use a plain name with no slashes."}
              </p>
            )}
          </div>
          {infosecEnabled && (
            <ChatInfosecLevelSelect
              id={infosecLevelSelectId}
              value={infosecLevelId}
              onChange={setInfosecLevelId}
              levels={infosecLevels}
              defaultLevel={defaultInfosecLevel}
              disabled={isLocked}
              compact
            />
          )}
          {controlTagsField}
          {destination ? (
            <div className="flex flex-col gap-1">
              <Label htmlFor={destinationSelectId} className="text-xs">
                Save to
              </Label>
              <Select
                value={saveToSystem ? "system" : "library"}
                onValueChange={(value) => setSaveToSystem(value === "system")}
                disabled={isLocked}
              >
                <SelectTrigger id={destinationSelectId} size="xs" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system" className="text-xs">
                    {destination.systemName} · {destination.branchName}
                  </SelectItem>
                  <SelectItem value="library" className="text-xs">
                    Your library
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      )}
      {action.staged ? (
        action.summary ? (
          <p className="text-muted-foreground mt-2 text-xs">{action.summary}</p>
        ) : null
      ) : (
        <>
          <pre className="bg-background/60 mt-2 max-h-48 max-w-full overflow-auto rounded-md p-2 font-mono text-xs [overflow-wrap:anywhere] whitespace-pre-wrap">
            {preview}
          </pre>
          {isTruncated ? (
            <Button
              type="button"
              variant="link"
              size="2xs"
              onClick={() => setExpanded((previous) => !previous)}
              className="text-muted-foreground h-auto px-0"
            >
              {expanded ? "Show less" : `Show all ${lines.length} lines`}
            </Button>
          ) : null}
        </>
      )}
      <div className="mt-2">
        <SaveResourceCardFooter state={state} fileName={fileName} canSave={canSave} onRequestSave={requestSave} />
      </div>
    </div>
  );
}

const PREVIEW_LINE_COUNT = 10;

interface SaveResourceCardFooterProps {
  state: SaveResourceCardState;
  fileName: string;
  canSave: boolean;
  onRequestSave: () => void;
}

function SaveResourceCardFooter({ state, fileName, canSave, onRequestSave }: SaveResourceCardFooterProps) {
  switch (state.status) {
    case "ready":
      return (
        <Button type="button" size="sm" onClick={onRequestSave} disabled={!canSave}>
          Save
        </Button>
      );
    case "saving":
      return (
        <Button type="button" size="sm" disabled>
          <Loader2Icon className="size-3.5 motion-safe:animate-spin" aria-hidden="true" />
          Saving…
        </Button>
      );
    case "saved":
      return (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <CircleCheckIcon className="text-success size-3.5 shrink-0" aria-hidden="true" />
            <span className="text-muted-foreground text-xs">
              {state.destination ? `Saved to ${state.destination}` : "Saved as new resource"}
            </span>
            <ChatEntityChip link={state.link} navigationPath={state.link.path}>
              {fileName}
            </ChatEntityChip>
          </div>
          {state.warning ? (
            <div className="flex items-center gap-2">
              <TriangleAlertIcon className="text-warning size-3.5 shrink-0" aria-hidden="true" />
              <span className="text-muted-foreground min-w-0 flex-1 text-xs">{state.warning}</span>
            </div>
          ) : null}
        </div>
      );
    case "error":
      return (
        <div className="flex items-center gap-2">
          <CircleAlertIcon className="text-destructive size-3.5 shrink-0" aria-hidden="true" />
          <span className="text-destructive min-w-0 flex-1 text-xs">{state.message}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRequestSave} disabled={!canSave}>
            Try again
          </Button>
        </div>
      );
  }
}
